import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

export const GET_FACILITY_SHIFT_COUNTS_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/calendar/facilityCount`;

const shiftTypeCounterSchema = z.object({
  am: z.number().optional(),
  pm: z.number().optional(),
  noc: z.number().optional(),
  custom: z.number().optional(),
  ori: z.number().optional(),
});

const dayCounterSchema = z.object({
  cancelled: z.number().optional(),
  filledCount: z.number().optional(),
  filledShifts: shiftTypeCounterSchema.optional(),
  openCount: z.number().optional(),
  openShifts: shiftTypeCounterSchema.optional(),
  total: z.number().optional(),
});

export type DayCounter = z.infer<typeof dayCounterSchema>;

// Response is a record of date strings (YYYY-MM-DD) to day counters
const facilityShiftCountsResponseSchema = z.record(z.string(), dayCounterSchema);

export type FacilityShiftCountsResponse = z.infer<typeof facilityShiftCountsResponseSchema>;

interface FacilityShiftCountsQueryParams {
  facilityId: string;
  dateFilter: {
    start: string;
    end: string;
  };
  timezone: string;
  agentReq?: string;
  enabled?: boolean;
}

/**
 * Creates query options for fetching facility shift counts.
 * Use this with `useQueries` when you need to make multiple parallel calls
 * (e.g., one per qualification for accurate filtering).
 */
export function createFacilityShiftCountsQueryOptions(params: FacilityShiftCountsQueryParams) {
  const { facilityId, dateFilter, timezone, agentReq, enabled = true } = params;

  return {
    queryKey: [
      GET_FACILITY_SHIFT_COUNTS_URL,
      facilityId,
      dateFilter.start,
      dateFilter.end,
      agentReq,
    ] as const,
    queryFn: async () => {
      const response = await get({
        url: GET_FACILITY_SHIFT_COUNTS_URL,
        queryParams: {
          facilityId,
          dateFilter,
          tmz: timezone,
          ...(agentReq ? { type: agentReq } : {}),
        },
        responseSchema: facilityShiftCountsResponseSchema,
      });

      return response.data;
    },
    enabled: enabled && Boolean(facilityId),
    staleTime: 60_000, // 1 minute
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_FACILITY_SHIFT_COUNTS_FAILURE,
    },
  };
}

interface UseGetFacilityShiftCountsParams {
  facilityId: string;
  dateFilter: {
    start: string;
    end: string;
  };
  timezone: string;
  agentReq?: string;
}

interface UseGetFacilityShiftCountsOptions {
  enabled?: boolean;
}

export function useGetFacilityShiftCounts(
  params: UseGetFacilityShiftCountsParams,
  options: UseGetFacilityShiftCountsOptions = {}
) {
  const { facilityId, dateFilter, timezone, agentReq } = params;
  const { enabled = true } = options;

  return useQuery({
    queryKey: [
      GET_FACILITY_SHIFT_COUNTS_URL,
      facilityId,
      dateFilter.start,
      dateFilter.end,
      agentReq,
    ],
    queryFn: async () => {
      const response = await get({
        url: GET_FACILITY_SHIFT_COUNTS_URL,
        queryParams: {
          facilityId,
          dateFilter,
          tmz: timezone,
          ...(agentReq ? { type: agentReq } : {}),
        },
        responseSchema: facilityShiftCountsResponseSchema,
      });

      return response.data;
    },
    enabled: enabled && Boolean(facilityId),
    staleTime: 60_000, // 1 minute
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_FACILITY_SHIFT_COUNTS_FAILURE,
    },
  });
}
